import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { apiSlice } from '@client/api';
import { AppDispatch, setSelectedTenant, SelectedTenant, selectAllowedTenants } from '../../redux';
import { useAppSelector } from '../useAppSelector';

const TENANT_STORAGE_KEY = 'exio.selectedTenant';

/**
 * Hook to synchronize tenant selection across browser tabs.
 * When a user switches tenant in one tab, all other tabs will:
 * 1. Update their Redux state with the new tenant
 * 2. Clear the API cache to prevent data leakage between tenants
 * 3. Refetch session to get updated user/person data for the new tenant
 */
export const useTenantSync = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Only handle changes to the tenant storage key
      if (event.key !== TENANT_STORAGE_KEY) return;
      
      // Ignore if the change originated from this tab
      if (event.storageArea !== localStorage) return;

      try {
        const newTenant: SelectedTenant = event.newValue 
          ? JSON.parse(event.newValue) 
          : null;
        
        // Update Redux state with the new tenant
        dispatch(setSelectedTenant(newTenant));
        
        // Clear API cache to prevent data leakage between tenants
        dispatch(apiSlice.util.resetApiState());
        
        // Refetch session to get updated user/person data for the new tenant
        dispatch(apiSlice.endpoints.getAuthSession.initiate() as never);
        
        console.debug('[TENANT SYNC] Tenant changed in another tab, cache cleared and session refetched');
      } catch (error) {
        console.error('[TENANT SYNC] Failed to parse tenant from storage:', error);
      }
    };

    // Add listener for storage events (fired when localStorage changes in another tab)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch]);
};
